export const getPercentage = (value: number, max: number, min: number = 0): number => {
  return ((value - min) * 100) / (max - min);
};

export const fromNumberToArray = (arrayLength: number): number[] => {
  return Array.from(Array(arrayLength), (_, currentValue) => currentValue + 1);
};

export const extractVatFromPrice = (price: number, vat: number): number => (price / 100) * (100 - vat);

export const getVatFromPrice = (price: number, vat: number): number => (price / 100) * vat;

export const firstOrDefault = <T>(array: T[], defaultValue: T): T => {
  return array.find((e) => Boolean(e)) ?? defaultValue;
};

/**
 * Computes quickly a non-cryptographic 32-bit hash.
 * Taken from https://github.com/garycourt/murmurhash-js - murmurhash3_gc.js
 * @param key
 */
export const getHashCode = (key: string): number => {
  const seed = 1;
  const c1 = 0xcc9e2d51;
  const c2 = 0x1b873593;
  const remainder = key.length & 3; // key.length % 4
  const bytes = key.length - remainder;
  let h1, h1b, k1, i;

  h1 = seed;
  i = 0;

  while (i < bytes) {
    k1 =
      (key.charCodeAt(i) & 0xff) |
      ((key.charCodeAt(++i) & 0xff) << 8) |
      ((key.charCodeAt(++i) & 0xff) << 16) |
      ((key.charCodeAt(++i) & 0xff) << 24);
    ++i;

    k1 = ((k1 & 0xffff) * c1 + ((((k1 >>> 16) * c1) & 0xffff) << 16)) & 0xffffffff;
    k1 = (k1 << 15) | (k1 >>> 17);
    k1 = ((k1 & 0xffff) * c2 + ((((k1 >>> 16) * c2) & 0xffff) << 16)) & 0xffffffff;

    h1 ^= k1;
    h1 = (h1 << 13) | (h1 >>> 19);
    h1b = ((h1 & 0xffff) * 5 + ((((h1 >>> 16) * 5) & 0xffff) << 16)) & 0xffffffff;
    h1 = (h1b & 0xffff) + 0x6b64 + ((((h1b >>> 16) + 0xe654) & 0xffff) << 16);
  }

  k1 = 0;

  switch (remainder) {
    case 3:
      break;
    case 2:
      break;
    case 1:
      k1 ^= key.charCodeAt(i) & 0xff;

      k1 = ((k1 & 0xffff) * c1 + ((((k1 >>> 16) * c1) & 0xffff) << 16)) & 0xffffffff;
      k1 = (k1 << 15) | (k1 >>> 17);
      k1 = ((k1 & 0xffff) * c2 + ((((k1 >>> 16) * c2) & 0xffff) << 16)) & 0xffffffff;
      h1 ^= k1;
  }

  h1 ^= key.length;

  h1 ^= h1 >>> 16;
  h1 = ((h1 & 0xffff) * 0x85ebca6b + ((((h1 >>> 16) * 0x85ebca6b) & 0xffff) << 16)) & 0xffffffff;
  h1 ^= h1 >>> 13;
  h1 = ((h1 & 0xffff) * 0xc2b2ae35 + ((((h1 >>> 16) * 0xc2b2ae35) & 0xffff) << 16)) & 0xffffffff;
  h1 ^= h1 >>> 16;

  return h1 >>> 0;
};

/**
 * Computes a price without vat.
 * @param {number} price Price with vat.
 * @param {number} vat Vat percentage value (e.g 21.0).
 * @return {number} Price without vat.
 * */
export const getPriceWithoutVat = (price: number, vat: number): number => {
  if (vat === 0) {
    return price;
  }

  const vatDivider = vat / 100 + 1;

  const result = price / vatDivider;
  return Number(result.toFixed(2));
};

/**
 * Computes a revenue price without vat from product.
 * It returns the price only when product does not have one of flag: freeProduct, action, upselling or discount.
 * @param {IProduct} product The product.
 * @return {number} Price without vat.
 */
export const getRevenueWithoutDiscount = (product: {
  freeProduct: boolean;
  action: boolean;
  upselling: boolean;
  discount: boolean;
  vat: number;
  priceWithVat: number;
  count: number;
}): number => {
  if (product.freeProduct || product.action || product.upselling || product.discount) {
    return 0;
  }

  return getProductPriceWithoutVat(product) * product.count;
};

/**
 * Computes a revenue price without vat from the product (taking quantity into account).
 * @param {IProduct} product The product.
 * @return {number} Price without vat.
 * */
export const getRevenue = (product: { vat: number; priceWithVat: number; count: number }): number => {
  return getProductPriceWithoutVat(product) * product.count;
};

/**
 * Computes a price without vat from product.
 * @param {IProduct} product The product.
 * @return {number} Price without vat.
 * */
export const getProductPriceWithoutVat = (product: { vat: number; priceWithVat: number }): number => {
  return getPriceWithoutVat(product.priceWithVat, product.vat);
};

export const getNumberInNegative = (value: number) => value && -Math.abs(value);
